import React, { useState, lazy } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleIcon, ChevronDownIcon, ArrowRightIcon, XCircleIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
const faqs = [{
  question: 'Are there long-term contracts?',
  answer: 'No. All our packages are month-to-month. We believe in earning your business every month, not locking you in. You can cancel anytime with 30 days notice.'
}, {
  question: 'Is the ad budget included in the monthly fee?',
  answer: "No, your ad spend is separate from our management fee. This gives you full control over your budget. We'll recommend a starting budget based on your market and goals."
}, {
  question: 'How often do I get reports?',
  answer: 'All clients receive monthly performance reports. Full Automation Suite clients also get monthly strategy calls to review results and plan next steps.'
}, {
  question: 'What kind of support do I get?',
  answer: 'All clients get email and chat support during business hours. Never Miss A Lead and Full Automation Suite clients get priority response times.'
}, {
  question: 'How quickly can I get started?',
  answer: 'Most clients are fully set up within 5-7 business days. We handle all the technical setup so you can focus on running your business.'
}, {
  question: 'Do I need to be tech-savvy?',
  answer: "Not at all. We handle everything technical. You'll get a simple dashboard and mobile app to manage your leads and appointments."
}];
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-200 last:border-0" data-id="element-643">
      <button className="w-full py-6 flex items-center justify-between text-left focus:outline-none" onClick={() => setIsOpen(!isOpen)} data-id="element-644">
        <span className="text-lg font-semibold text-gray-900" data-id="element-645">{question}</span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.2
      }} className="flex-shrink-0 ml-4 text-viridia" data-id="element-646">
          <ChevronDownIcon className="w-5 h-5" data-id="element-647" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-648">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-649">
            <p className="pb-6 text-gray-600 leading-relaxed" data-id="element-650">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export function PricingPage() {
  return <div className="bg-viridia-black min-h-screen pt-24" data-id="element-651">
      <SEOHead title="Pricing | Marketing Packages for Local Businesses | Viridia Analytics Phoenix" description="Transparent, month-to-month pricing for ad campaign management, marketing automations, and web design. No long-term contracts. Packages starting for Phoenix local service businesses." canonical="/pricing" keywords="marketing agency pricing, ad management pricing Phoenix, marketing automation cost, web design pricing, local business marketing packages" data-id="element-652" />
      {/* HERO SECTION */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center" data-id="element-653">
        <ScrollReveal data-id="element-654">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 text-viridia text-sm font-semibold tracking-wide uppercase mb-6 border border-viridia/20" data-id="element-655">
            CRM & AUTOMATION
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 tracking-tight" data-id="element-656">
            Automation Systems That{' '}
            <span className="text-viridia" data-id="element-657">Run Your Business</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto" data-id="element-658">
            Month-to-month systems that capture leads, automate follow-up, and
            keep your business organized — so you can focus on the work.
          </p>
        </ScrollReveal>
      </section>

      {/* PRICING CARDS SECTION */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-659">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center" data-id="element-660">
          {/* Card 1: Business Essentials */}
          <ScrollReveal delay={0.1} data-id="element-661">
            <div className="bg-viridia-surface rounded-2xl p-8 border border-gray-800 hover:shadow-2xl hover:shadow-viridia/5 transition-all duration-300 h-full flex flex-col" data-id="element-662">
              <div className="mb-8" data-id="element-663">
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-664">
                  Business Essentials
                </h3>
                <p className="text-gray-400 text-sm h-10" data-id="element-665">
                  The solid foundation: CRM, scheduling, and professional
                  billing.
                </p>
              </div>
              <div className="mb-8" data-id="element-666">
                <span className="text-5xl font-extrabold text-white" data-id="element-667">$497</span>
                <span className="text-gray-500 font-medium" data-id="element-668">/month</span>
              </div>
              <ul className="space-y-4 mb-8 flex-grow" data-id="element-669">
                {['CRM Setup & Contact Management', 'Online Booking / Calendar Integration', 'Professional Branded Invoicing & Revenue Tracking', 'QuickBooks & Local Billing Integration', 'Lead Capture Workflows', 'GHL Mobile App Access'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-670">
                    <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-671" />
                    <span className="text-gray-300 text-sm" data-id="element-672">{feature}</span>
                  </li>)}
              </ul>
              <Link to="/contact" className="w-full py-3 px-4 rounded-lg font-semibold text-center transition-all duration-300 border border-gray-600 text-white hover:bg-gray-800" data-id="element-673">
                Get Started
              </Link>
            </div>
          </ScrollReveal>

          {/* Card 2: Never Miss A Lead (MOST POPULAR) */}
          <ScrollReveal delay={0.2} data-id="element-674">
            <div className="bg-viridia-surface rounded-2xl p-8 border-2 border-viridia relative transform lg:scale-105 shadow-2xl shadow-viridia/10 h-full flex flex-col z-10" data-id="element-675">
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-viridia text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider" data-id="element-676">
                Most Popular
              </div>
              <div className="mb-8 mt-2" data-id="element-677">
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-678">
                  Never Miss A Lead
                </h3>
                <p className="text-gray-400 text-sm h-10" data-id="element-679">
                  For busy contractors who lose jobs every time a call goes
                  unanswered.
                </p>
              </div>
              <div className="mb-8" data-id="element-680">
                <span className="text-5xl font-extrabold text-white" data-id="element-681">$897</span>
                <span className="text-gray-500 font-medium" data-id="element-682">/month</span>
              </div>
              <ul className="space-y-4 mb-8 flex-grow" data-id="element-683">
                <li className="flex items-start" data-id="element-684">
                  <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-685" />
                  <span className="text-white font-medium text-sm" data-id="element-686">
                    Everything in Business Essentials
                  </span>
                </li>
                {['Missed Call Text-Back Automation', 'AI Voice Receptionist (backup)', 'A2P Registration & Business Number Setup', 'Automated Follow-Up Sequences', 'Unified Communications Hub'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-687">
                    <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-688" />
                    <span className="text-gray-300 text-sm" data-id="element-689">{feature}</span>
                  </li>)}
              </ul>
              <Link to="/contact" className="w-full py-3 px-4 rounded-lg font-semibold text-center transition-all duration-300 bg-viridia hover:bg-viridia-dark text-white shadow-lg shadow-viridia/25" data-id="element-690">
                Get Started
              </Link>
            </div>
          </ScrollReveal>

          {/* Card 3: Full Automation Suite */}
          <ScrollReveal delay={0.3} data-id="element-691">
            <div className="bg-viridia-surface rounded-2xl p-8 border border-gray-800 hover:shadow-2xl hover:shadow-viridia/5 transition-all duration-300 h-full flex flex-col" data-id="element-692">
              <div className="mb-8" data-id="element-693">
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-694">
                  Full Automation Suite
                </h3>
                <p className="text-gray-400 text-sm h-10" data-id="element-695">
                  The full system. Your business runs on autopilot.
                </p>
              </div>
              <div className="mb-8" data-id="element-696">
                <span className="text-5xl font-extrabold text-white" data-id="element-697">
                  $1,497
                </span>
                <span className="text-gray-500 font-medium" data-id="element-698">/month</span>
              </div>
              <ul className="space-y-4 mb-8 flex-grow" data-id="element-699">
                <li className="flex items-start" data-id="element-700">
                  <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-701" />
                  <span className="text-white font-medium text-sm" data-id="element-702">
                    Everything in Never Miss A Lead
                  </span>
                </li>
                {['AI Chatbot', 'Reputation Management', 'Review Automation', 'Pipeline Automation', 'Monthly Strategy Calls'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-703">
                    <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-704" />
                    <span className="text-gray-300 text-sm" data-id="element-705">{feature}</span>
                  </li>)}
              </ul>
              <Link to="/contact" className="w-full py-3 px-4 rounded-lg font-semibold text-center transition-all duration-300 border border-gray-600 text-white hover:bg-gray-800" data-id="element-706">
                Get Started
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* AD MANAGEMENT ADD-ON */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-707">
        <ScrollReveal direction="up" data-id="element-708">
          <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-709">
            <div data-id="element-710">
              <h3 className="text-2xl font-bold text-white mb-2" data-id="element-711">
                Need Ad Management Too?
              </h3>
              <p className="text-gray-400 max-w-2xl" data-id="element-712">
                Ad campaign management can be added to any package. Pricing
                depends on platforms, ad spend, and scope. Book a call to get a
                custom quote.
              </p>
            </div>
            <Link to="/contact" className="inline-flex items-center text-viridia hover:text-viridia-dark font-semibold transition-colors whitespace-nowrap" data-id="element-713">
              Get a Custom Ad Quote <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-714" />
            </Link>
          </div>
        </ScrollReveal>
      </section>

      {/* WEBSITE PRICING SECTION */}
      <section id="web-design" className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-715">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-716">
          <ScrollReveal data-id="element-717">
            <div className="text-center mb-16" data-id="element-718">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 text-viridia text-sm font-semibold tracking-wide uppercase mb-6 border border-viridia/20" data-id="element-719">
                WEB DESIGN & SEO
              </div>
              <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4" data-id="element-720">
                Website Packages
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto" data-id="element-721">
                High-converting, mobile-first websites built for local service
                businesses. One-time investment.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center" data-id="element-722">
            {/* Starter Site */}
            <ScrollReveal delay={0.1} data-id="element-723">
              <div className="bg-viridia-surface rounded-2xl p-8 border border-gray-800 hover:shadow-2xl hover:shadow-viridia/5 transition-all duration-300 h-full flex flex-col" data-id="element-724">
                <div className="mb-8" data-id="element-725">
                  <h3 className="text-2xl font-bold text-white mb-2" data-id="element-726">
                    Starter Site
                  </h3>
                  <p className="text-gray-400 text-sm h-10" data-id="element-727">
                    The essentials to get your business online and looking
                    professional.
                  </p>
                </div>
                <div className="mb-8" data-id="element-728">
                  <span className="text-4xl font-extrabold text-white" data-id="element-729">
                    $1,200–$1,800
                  </span>
                  <span className="text-gray-500 font-medium ml-2" data-id="element-730">
                    one-time
                  </span>
                </div>
                <ul className="space-y-4 mb-8 flex-grow" data-id="element-731">
                  {['3 pages (Home, Services, Contact)', 'Mobile responsive', 'Contact form', 'On-page SEO', 'Google Analytics setup'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-732">
                      <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-733" />
                      <span className="text-gray-300 text-sm" data-id="element-734">{feature}</span>
                    </li>)}
                </ul>
                <div className="mt-auto" data-id="element-735">
                  <Link to="/contact" className="block w-full py-3 px-4 rounded-lg font-semibold text-center transition-all duration-300 border border-gray-600 text-white hover:bg-gray-800 mb-3" data-id="element-736">
                    Get a Quote
                  </Link>
                  <p className="text-center text-gray-500 text-xs" data-id="element-737">
                    One-time fee. No hidden costs.
                  </p>
                </div>
              </div>
            </ScrollReveal>

            {/* Growth Site (MOST POPULAR) */}
            <ScrollReveal delay={0.2} data-id="element-738">
              <div className="bg-viridia-surface rounded-2xl p-8 border-2 border-viridia relative transform lg:scale-105 shadow-2xl shadow-viridia/10 h-full flex flex-col z-10" data-id="element-739">
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-viridia text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider" data-id="element-740">
                  Most Popular
                </div>
                <div className="mb-8 mt-2" data-id="element-741">
                  <h3 className="text-2xl font-bold text-white mb-2" data-id="element-742">
                    Growth Site
                  </h3>
                  <p className="text-gray-400 text-sm h-10" data-id="element-743">
                    Built to convert traffic into leads and dominate local
                    search.
                  </p>
                </div>
                <div className="mb-8" data-id="element-744">
                  <span className="text-4xl font-extrabold text-white" data-id="element-745">
                    $2,500–$3,000
                  </span>
                  <span className="text-gray-500 font-medium ml-2" data-id="element-746">
                    one-time
                  </span>
                </div>
                <ul className="space-y-4 mb-8 flex-grow" data-id="element-747">
                  {['5–7 pages', 'Custom design', 'Service-specific pages', 'Review/trust section', 'Conversion CTAs', 'On-page SEO baked in'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-748">
                      <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-749" />
                      <span className="text-gray-300 text-sm" data-id="element-750">{feature}</span>
                    </li>)}
                </ul>
                <div className="mt-auto" data-id="element-751">
                  <Link to="/contact" className="block w-full py-3 px-4 rounded-lg font-semibold text-center transition-all duration-300 bg-viridia hover:bg-viridia-dark text-white shadow-lg shadow-viridia/25 mb-3" data-id="element-752">
                    Get a Quote
                  </Link>
                  <p className="text-center text-gray-400 text-xs" data-id="element-753">
                    One-time fee. No hidden costs.
                  </p>
                </div>
              </div>
            </ScrollReveal>

            {/* Premium Site */}
            <ScrollReveal delay={0.3} data-id="element-754">
              <div className="bg-viridia-surface rounded-2xl p-8 border border-gray-800 hover:shadow-2xl hover:shadow-viridia/5 transition-all duration-300 h-full flex flex-col" data-id="element-755">
                <div className="mb-8" data-id="element-756">
                  <h3 className="text-2xl font-bold text-white mb-2" data-id="element-757">
                    Premium Site
                  </h3>
                  <p className="text-gray-400 text-sm h-10" data-id="element-758">
                    The ultimate digital storefront for established businesses.
                  </p>
                </div>
                <div className="mb-8" data-id="element-759">
                  <span className="text-4xl font-extrabold text-white" data-id="element-760">
                    $4,000–$6,000
                  </span>
                  <span className="text-gray-500 font-medium ml-2" data-id="element-761">
                    one-time
                  </span>
                </div>
                <ul className="space-y-4 mb-8 flex-grow" data-id="element-762">
                  {['8–12 pages', 'Full custom design', 'Blog setup', 'Multiple service pages', 'Schema markup', 'Lead capture', 'Speed optimization'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-763">
                      <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-764" />
                      <span className="text-gray-300 text-sm" data-id="element-765">{feature}</span>
                    </li>)}
                </ul>
                <div className="mt-auto" data-id="element-766">
                  <Link to="/contact" className="block w-full py-3 px-4 rounded-lg font-semibold text-center transition-all duration-300 border border-gray-600 text-white hover:bg-gray-800 mb-3" data-id="element-767">
                    Get a Quote
                  </Link>
                  <p className="text-center text-gray-500 text-xs" data-id="element-768">
                    One-time fee. No hidden costs.
                  </p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* LOCAL SEO ADD-ONS */}
      <section className="py-16 bg-viridia-black" data-id="element-769">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-770">
          <ScrollReveal data-id="element-771">
            <div className="text-center mb-12" data-id="element-772">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4" data-id="element-773">
                Local SEO Add-Ons
              </h3>
              <p className="text-gray-400 max-w-2xl mx-auto" data-id="element-774">
                Ongoing optimization to ensure your new website actually gets
                found by local customers.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-775">
            {/* Basic */}
            <ScrollReveal delay={0.1} data-id="element-776">
              <div className="bg-viridia-surface rounded-xl p-6 border border-gray-800 h-full flex flex-col" data-id="element-777">
                <h4 className="text-xl font-bold text-white mb-2" data-id="element-778">
                  Basic Local SEO
                </h4>
                <div className="mb-6" data-id="element-779">
                  <span className="text-2xl font-bold text-viridia" data-id="element-780">
                    $300–$500
                  </span>
                  <span className="text-gray-500 text-sm" data-id="element-781">/mo</span>
                </div>
                <ul className="space-y-3 mb-6 flex-grow" data-id="element-782">
                  {['GBP optimization', 'Citations', 'Monthly report'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-783">
                        <CheckCircleIcon className="w-4 h-4 text-viridia mr-2 flex-shrink-0 mt-1" data-id="element-784" />
                        <span className="text-gray-300 text-sm" data-id="element-785">{feature}</span>
                      </li>)}
                </ul>
              </div>
            </ScrollReveal>

            {/* Standard */}
            <ScrollReveal delay={0.2} data-id="element-786">
              <div className="bg-viridia-surface rounded-xl p-6 border border-gray-800 h-full flex flex-col" data-id="element-787">
                <h4 className="text-xl font-bold text-white mb-2" data-id="element-788">
                  Standard Local SEO
                </h4>
                <div className="mb-6" data-id="element-789">
                  <span className="text-2xl font-bold text-viridia" data-id="element-790">
                    $500–$800
                  </span>
                  <span className="text-gray-500 text-sm" data-id="element-791">/mo</span>
                </div>
                <ul className="space-y-3 mb-6 flex-grow" data-id="element-792">
                  <li className="flex items-start" data-id="element-793">
                    <CheckCircleIcon className="w-4 h-4 text-viridia mr-2 flex-shrink-0 mt-1" data-id="element-794" />
                    <span className="text-white font-medium text-sm" data-id="element-795">
                      Everything in Basic
                    </span>
                  </li>
                  {['On-page SEO', 'Schema markup', 'Review management'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-796">
                        <CheckCircleIcon className="w-4 h-4 text-viridia mr-2 flex-shrink-0 mt-1" data-id="element-797" />
                        <span className="text-gray-300 text-sm" data-id="element-798">{feature}</span>
                      </li>)}
                </ul>
              </div>
            </ScrollReveal>

            {/* Full */}
            <ScrollReveal delay={0.3} data-id="element-799">
              <div className="bg-viridia-surface rounded-xl p-6 border border-gray-800 h-full flex flex-col" data-id="element-800">
                <h4 className="text-xl font-bold text-white mb-2" data-id="element-801">
                  Full Local SEO
                </h4>
                <div className="mb-6" data-id="element-802">
                  <span className="text-2xl font-bold text-viridia" data-id="element-803">
                    $800–$1,200
                  </span>
                  <span className="text-gray-500 text-sm" data-id="element-804">/mo</span>
                </div>
                <ul className="space-y-3 mb-6 flex-grow" data-id="element-805">
                  <li className="flex items-start" data-id="element-806">
                    <CheckCircleIcon className="w-4 h-4 text-viridia mr-2 flex-shrink-0 mt-1" data-id="element-807" />
                    <span className="text-white font-medium text-sm" data-id="element-808">
                      Everything in Standard
                    </span>
                  </li>
                  {['Citation building', 'GBP posts & content', 'City pages', 'Competitor tracking'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-809">
                      <CheckCircleIcon className="w-4 h-4 text-viridia mr-2 flex-shrink-0 mt-1" data-id="element-810" />
                      <span className="text-gray-300 text-sm" data-id="element-811">{feature}</span>
                    </li>)}
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* HUBSPOT SEO CERTIFICATION */}
      <section className="py-16 bg-viridia-black" data-id="element-812">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-813">
          <ScrollReveal data-id="element-814">
            <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-center gap-8 md:gap-12" data-id="element-815">
              <div className="flex-shrink-0 w-full md:w-auto flex justify-center" data-id="element-816">
                <div className="relative group" data-id="element-817">
                  <div className="absolute -inset-1 bg-viridia/20 rounded-xl blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-818"></div>
                  <img src="https://cdn.magicpatterns.com/uploads/it67T9N3RYnBZ32NVuR62H/SEOcertified.png" alt="HubSpot Academy SEO Certification — Kevin Rogers" loading="lazy" className="relative w-64 md:w-72 rounded-lg shadow-xl border border-gray-700" data-id="element-819" />
                </div>
              </div>
              <div className="text-center md:text-left" data-id="element-820">
                <div className="flex items-center justify-center md:justify-start gap-3 mb-4" data-id="element-821">
                  <svg viewBox="0 0 512 512" className="w-8 h-8" aria-label="HubSpot logo" data-id="element-822">
                    <path fill="#FF7A59" d="M380.48 256c0-19.2-9.6-36.48-24.96-47.04V161.6c0-7.68-6.24-13.92-13.92-13.92h-.96c-7.68 0-13.92 6.24-13.92 13.92v40.32c-5.28-1.92-10.88-3.2-16.64-3.52V136c0-17.28-14.08-31.36-31.36-31.36-17.28 0-31.36 14.08-31.36 31.36v62.4c-5.76.32-11.36 1.6-16.64 3.52V161.6c0-7.68-6.24-13.92-13.92-13.92h-.96c-7.68 0-13.92 6.24-13.92 13.92v47.36C186.72 219.52 177.12 236.8 177.12 256c0 29.44 19.52 54.4 46.24 62.72l-24.64 24.64c-3.84 3.84-3.84 10.08 0 13.92 1.92 1.92 4.48 2.88 6.96 2.88s5.04-.96 6.96-2.88l31.04-31.04c5.28 1.6 10.88 2.56 16.64 2.88v44.48c0 17.28 14.08 31.36 31.36 31.36 17.28 0 31.36-14.08 31.36-31.36v-44.48c5.76-.32 11.36-1.28 16.64-2.88l31.04 31.04c1.92 1.92 4.48 2.88 6.96 2.88s5.04-.96 6.96-2.88c3.84-3.84 3.84-10.08 0-13.92l-24.64-24.64C360.96 310.4 380.48 285.44 380.48 256zM278.72 296.96c-22.56 0-40.96-18.4-40.96-40.96s18.4-40.96 40.96-40.96 40.96 18.4 40.96 40.96-18.4 40.96-40.96 40.96z" data-id="element-823" />
                  </svg>
                  <span className="text-sm font-bold text-[#FF7A59] tracking-wide uppercase" data-id="element-824">
                    HubSpot Academy
                  </span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-3" data-id="element-825">
                  SEO Certified
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-826">
                  Our team is HubSpot SEO Certified — trained and tested on
                  technical SEO, on-page optimization, link building, and search
                  strategy. Not guesswork. Proven methodology.
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FREELANCER COMPARISON */}
      <section className="py-20 bg-viridia-black border-t border-gray-800" data-id="element-827">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-828">
          <ScrollReveal data-id="element-829">
            <div className="text-center mb-12" data-id="element-830">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-831">
                Why Not Just Hire a Freelancer?
              </h2>
              <p className="text-lg text-gray-400" data-id="element-832">
                You get what you pay for. We build systems that generate
                revenue, not just pretty brochures.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-833">
            <ScrollReveal delay={0.1} data-id="element-834">
              <div className="bg-viridia-surface/50 rounded-2xl p-8 border border-red-900/30 h-full" data-id="element-835">
                <h3 className="text-xl font-bold text-gray-300 mb-6 flex items-center" data-id="element-836">
                  <span className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center mr-3" data-id="element-837">
                    <XCircleIcon className="w-5 h-5 text-red-400" data-id="element-838" />
                  </span>
                  The Average Freelancer
                </h3>
                <ul className="space-y-4" data-id="element-839">
                  {['Cheap, bloated templates', 'No marketing or conversion strategy', 'Zero accountability for results', 'Disappears after launch'].map((item, i) => <li key={i} className="flex items-start" data-id="element-840">
                      <XCircleIcon className="w-5 h-5 text-red-400 mr-3 flex-shrink-0 mt-0.5 opacity-70" data-id="element-841" />
                      <span className="text-gray-400" data-id="element-842">{item}</span>
                    </li>)}
                </ul>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-843">
              <div className="bg-viridia-surface rounded-2xl p-8 border border-viridia/30 h-full shadow-lg shadow-viridia/5" data-id="element-844">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center" data-id="element-845">
                  <span className="w-8 h-8 rounded-full bg-viridia/10 flex items-center justify-center mr-3" data-id="element-846">
                    <CheckCircleIcon className="w-5 h-5 text-viridia" data-id="element-847" />
                  </span>
                  Viridia Analytics
                </h3>
                <ul className="space-y-4" data-id="element-848">
                  {['Conversion-focused, custom builds', 'Deep local SEO knowledge', 'Ongoing support and optimization', 'Built specifically for your market'].map((item, i) => <li key={i} className="flex items-start" data-id="element-849">
                      <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-850" />
                      <span className="text-gray-200 font-medium" data-id="element-851">{item}</span>
                    </li>)}
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-light text-gray-900" data-id="element-852">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-853">
          <ScrollReveal data-id="element-854">
            <div className="text-center mb-16" data-id="element-855">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-856">
                Frequently Asked Questions
              </h2>
              <p className="text-gray-600" data-id="element-857">
                Everything you need to know about our packages and billing.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2} data-id="element-858">
            <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 p-6 md:p-8 border border-gray-100" data-id="element-859">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-860" />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-gradient-to-br from-viridia to-viridia-dark relative overflow-hidden" data-id="element-861">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay" data-id="element-862"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-863">
          <ScrollReveal data-id="element-864">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-865">
              Not Sure Which Package Is Right?
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto" data-id="element-866">
              Book a free call and we'll recommend the perfect plan based on
              your business size and goals.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-lg bg-white text-viridia-dark hover:bg-gray-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1" data-id="element-867">
              Book Your Free Consultation
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}